// app/providers.js
'use client';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  Telemetry,
  createPostHogAdapter,
  createSentryAdapter,
} from '@/lib/telemetry';

// PostHogUserIdentifier component moved from main.tsx
function PostHogUserIdentifier() {
  const auth = useAuthStore();

  useEffect(() => {
    if (auth.user?.id) {
      try {
        // Fans out to both PostHog and Sentry via the telemetry bus
        Telemetry.identify({
          id: auth.user.id,
          email: auth.user.email,
          name: `${auth.user.firstName} ${auth.user.lastName}`,
          orgName: auth.companyName ?? undefined,
          tenant: auth.tenant?.tenantName ?? undefined,
          roles: auth.roles,
        });

        // PostHog-specific properties that don't belong in the generic TelemetryUser
        posthog.people.set({
          isMonitoring: auth.roles.includes('Monitoring'),
          isProjectAdmin: auth.roles.includes('ProjectAdmin'),
          isCompanyAdmin: auth.roles.includes('CompanyAdmin'),
          isSuperAdmin: auth.roles.includes('SuperAdmin'),
        });
      } catch (error) {
        console.error('Error identifying user:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.id]);

  return null;
}

export function PHProvider({ children }: { children: React.ReactNode }) {
  return (
    <PostHogProvider
      apiKey={'phc_F4ygX6UHUfM9N6xzwtnGwJ7DI7mmJnvzYEiSNVccfWx'}
      options={{
        api_host: 'https://walrus.watchful.co.nz',
        autocapture: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        before_send: (eventProperties: any) => {
          const props = { ...eventProperties };

          const originalUrl = props.$current_url || props.url || props.request_url || '';

          try {
            const urlObj = new URL(originalUrl);

            // Only sanitize specific endpoint
            if (urlObj.href.includes('api.admin.watchful.cloud/Auth/tenants')) {
              urlObj.searchParams.set('email', '[REDACTED]');
              urlObj.searchParams.set('password', '[REDACTED]');
              const sanitizedUrl = urlObj.toString();

              if (props.$current_url) props.$current_url = sanitizedUrl;
              if (props.url) props.url = sanitizedUrl;
              if (props.request_url) props.request_url = sanitizedUrl;
            }
          } catch (e) {
            // URL parsing failed – ignore
          }

          return props;
        },
        capture_pageview: false,
        person_profiles: 'identified_only',
        loaded: (posthog) => {
          posthog.debug(false);
          // Expose for debug bookmarklet
          (window as unknown as { posthog: typeof posthog }).posthog = posthog;

          // Initialize the telemetry bus with both adapters
          Telemetry.init({
            adapters: [createPostHogAdapter(), createSentryAdapter()],
          });
        },
        bootstrap: {
          featureFlags: {
            'watchful-only': false, // Set default value for development
            'quick-actions': false,
            'new-timeline': false,
            'admin-portal': false,
            'one-link': false,
            'dashboard-v2': false,
            'object-sensitivity-slider': false,
            'announcement-reporting': false,
          },
        },
        persistence: 'localStorage',
      }}
    >
      <PostHogUserIdentifier />
      {children}
    </PostHogProvider>
  );
}
